import { Card } from '@/types/Card';
import {
  CARD_KEYS,
  CHANNEL_KEYS,
  CHANNEL_NAMES,
  BANK_KEYS,
  BANK_NAMES,
} from '../../constants/keys';
import { FeatureType } from '@/types/Feature';
import { CardLinkMapping, BankLinkMapping } from '../cardLinks';

/**
 * Map of bank keys to their source URLs
 * This allows us to easily update bank information when they change their websites
 * The key is the bank key from BANK_KEYS
 */
export const bankLinkMap: Record<string, BankLinkMapping> = {
  [BANK_KEYS.DBS]: {
    sourceUrl:
      'https://www.dbs.com.tw/personal-zh/cards/default.page?pid=tw-pweb-personal_zh-cards-header',
    lastUpdated: '2025-05-04',
  },
};

/**
 * Map of card keys to their source URLs
 * This allows us to easily update card information when banks change their offerings
 * The key is the card key from CARD_KEYS
 */
export const cardLinkMap: Record<string, CardLinkMapping> = {
  // DBS Bank cards
  [CARD_KEYS.DBS_ECO]: {
    sourceUrl: 'https://www.dbs.com.tw/personal-zh/cards/dbs_eco/index.html',
    applyUrl:
      'https://internet-banking.dbs.com.tw/credit-card-application/?cardlogo=310&sc=0c40b28ad098bab25f32d395acc2ca47',
    lastUpdated: '2025-08-31',
  },
  [CARD_KEYS.DBS_TRAVEL_WORLD_ELITE]: {
    sourceUrl: 'https://www.dbs.com.tw/personal-zh/cards/travel_world_elite/index.html',
    applyUrl:
      'https://internet-banking.dbs.com.tw/credit-card-application/?cardlogo=310&sc=0c40b28ad098bab25f32d395acc2ca47',
    lastUpdated: '2025-08-31',
  },
  [CARD_KEYS.DBS_FUNLIFE]: {
    sourceUrl: 'https://www.dbs.com.tw/personal-zh/cards/funlife/index.html',
    applyUrl:
      'https://internet-banking.dbs.com.tw/credit-card-application/?cardlogo=310&sc=0c40b28ad098bab25f32d395acc2ca47',
    lastUpdated: '2025-08-31',
  },
  [CARD_KEYS.DBS_TRAVEL_WORLD]: {
    sourceUrl: 'https://www.dbs.com.tw/personal-zh/cards/travelworld/index.html',
    applyUrl:
      'https://internet-banking.dbs.com.tw/credit-card-application/?cardlogo=310&sc=0c40b28ad098bab25f32d395acc2ca47',
    lastUpdated: '2025-08-31',
  },
};

export const dbsCards: { [key: string]: Card } = {
  [CARD_KEYS.DBS_TRAVEL_WORLD_ELITE]: {
    name: '星展飛行世界卡世界卡',
    summary: '海外消費2.5%回饋，指定通路最高5%',
    issuing_bank: BANK_NAMES[BANK_KEYS.DBS],
    payment_network: 'Mastercard',
    picture: 'logo_dbs-travel-world-elite.webp',
    key: 'dbs-travel-world-elite',
    link: cardLinkMap[CARD_KEYS.DBS_TRAVEL_WORLD_ELITE].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.DBS_TRAVEL_WORLD_ELITE].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '2.5% 現金回饋',
        max_feedback: '無上限',
        simplified_condition: '海外消費',
        condition: '海外消費享2.5%現金回饋，無上限',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.OVERSEAS],
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '5% 回饋',
        max_feedback: '每月最高回饋1,000元',
        simplified_condition: '指定旅遊平台',
        condition: '於Agoda、Booking.com、Klook等指定旅遊平台消費享5%回饋，每月最高回饋1,000元',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.TRAVEL],
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場貴賓室',
        max_feedback: '每年6次',
        simplified_condition: 'Priority Pass',
        condition: '持卡人可享有Priority Pass會員資格，每年可免費使用全球機場貴賓室6次',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
    ],
    domestic_feedback: '1%',
    international_feedback: '2.5%',
    specific_channel_feedback: '5%',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.DBS_FUNLIFE]: {
    name: '星展豐盛無限卡',
    summary: '指定通路最高5%回饋，機場接送服務',
    issuing_bank: BANK_NAMES[BANK_KEYS.DBS],
    payment_network: 'Mastercard',
    picture: 'logo_dbs-funlife.webp',
    key: 'dbs-funlife',
    link: cardLinkMap[CARD_KEYS.DBS_FUNLIFE].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.DBS_FUNLIFE].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '5% 回饋',
        max_feedback: '每月最高回饋1,500元',
        simplified_condition: '指定餐廳、百貨公司',
        condition: '於指定高級餐廳、百貨公司消費享5%回饋，每月最高回饋1,500元',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.DINING],
      },
      {
        type: FeatureType.TRAVEL,
        feedback: '機場接送',
        max_feedback: '每年4次',
        simplified_condition: '單筆消費滿NT$10,000',
        condition: '刷卡單筆消費滿NT$10,000，可享每年4次機場接送服務',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.TRANSPORTATION],
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場貴賓室',
        max_feedback: '無限次使用',
        simplified_condition: 'Priority Pass',
        condition: '持卡人可享有Priority Pass會員資格，享全球1,300多間機場貴賓室無限次使用',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
    ],
    domestic_feedback: '1.5%',
    international_feedback: '2%',
    specific_channel_feedback: '5%',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.DBS_TRAVEL_WORLD]: {
    name: '星展飛行世界卡',
    summary: '海外消費2%回饋，指定旅遊通路3%',
    issuing_bank: BANK_NAMES[BANK_KEYS.DBS],
    payment_network: 'VISA',
    picture: 'logo_dbs-travel-world.webp',
    key: 'dbs-travelworld',
    link: cardLinkMap[CARD_KEYS.DBS_TRAVEL_WORLD].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.DBS_TRAVEL_WORLD].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '2% 現金回饋',
        max_feedback: '無上限',
        simplified_condition: '海外消費',
        condition: '海外消費享2%現金回饋，無上限',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.OVERSEAS],
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% 回饋',
        max_feedback: '每月最高回饋800元',
        simplified_condition: '指定旅遊平台',
        condition: '於Agoda、Booking.com、Klook等指定旅遊平台消費享3%回饋，每月最高回饋800元',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.TRAVEL],
      },
      {
        type: FeatureType.AIRPORT_SERVICE,
        feedback: '機場貴賓室',
        max_feedback: '每年2次',
        simplified_condition: 'Priority Pass',
        condition: '持卡人可享有Priority Pass會員資格，每年可免費使用全球機場貴賓室2次',
        channel: CHANNEL_NAMES[CHANNEL_KEYS.AIRPORT_LOUNGE],
      },
    ],
    domestic_feedback: '0.8%',
    international_feedback: '2%',
    specific_channel_feedback: '3%',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.DBS_ECO]: {
    name: '星展eco永續卡',
    summary: '指定日本商店消費8.5%優惠',
    issuing_bank: BANK_NAMES[BANK_KEYS.DBS],
    payment_network: 'VISA',
    picture: 'logo_dbs-eco.jpeg',
    key: 'dbs-eco',
    link: cardLinkMap[CARD_KEYS.DBS_ECO].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.DBS_ECO].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '最高9.8%回饋',
        max_feedback: '回饋無上限，於當期帳單直接折抵',
        simplified_condition: '日韓泰/新加坡／美歐洲 最高9.8%回饋',
        condition: '日韓泰／新加坡／美洲／歐洲一般消費享9.8%回饋，無上限',
        channel: '日韓泰／新加坡／美洲／歐洲一般消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '6% 現金',
        max_feedback: '回饋無上限',
        simplified_condition: '國內/外一般消費 最高6%回饋',
        condition: '',
        channel: '國內/外一般消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1.2% 現金積點回饋',
        max_feedback: '回饋無上限',
        simplified_condition: '國內/外一般消費享 1.2% 現金積點回饋',
        condition: '國內/外一般消費享 1.2% 現金積點回饋',
        channel: '國內/外一般消費',
      },
    ],
    domestic_feedback: '1.5%',
    international_feedback: '2.5%',
    specific_channel_feedback: '3%',
    start_date: '2025-01-01',
    end_date: '2025-08-31',
  },
};
